import { FieldConfig } from "../types/formTypes";
import {
  personalInfoFields,
  employmentInfoFields,
  vendorInfoFields,
  courseInfoFields,
} from "./tuitionFormConfig";

const nonEditableHelperText: string =
  "Read only; this value is assigned automatically by HR";

function lockedFields(fields: FieldConfig[]): FieldConfig[] {
  return fields.map((field) => {
    const base: FieldConfig = {
      ...field,
      disabled: true,
      helperText: nonEditableHelperText,
      required: false,
      // render selects and date inputs as text in read-only views so formatted strings display
      type:
        field.type === "select" || field.type === "date" ? "text" : field.type,
      placeholder: "",
    } as FieldConfig;

    // Normalize search button flag (some configs use isSearchBtnRequired)
    (base as any).isSearchBtnRequired =
      field.name === "alternateNuid"
        ? false
        : (field as any).isSearchBtnRequired;

    return base;
  });
}

export const ApplicantInformationFields: FieldConfig[] =
  lockedFields(personalInfoFields);

export const EmploymentInformationFields: FieldConfig[] =
  lockedFields(employmentInfoFields);

export const SchoolInformationFields: FieldConfig[] =
  lockedFields(vendorInfoFields);

export const ClassInformationFields: FieldConfig[] =
  lockedFields(courseInfoFields);

function findValueRecursively(obj: any, key: string): any {
  if (!obj || typeof obj !== "object") {
    return undefined;
  }
  
  if (Object.hasOwn(obj, key)) {
    return obj[key];
  }
  
  for (const k of Object.keys(obj)) {
    const val = obj[k];
    if (val && typeof val === "object") {
      const found = findValueRecursively(val, key);
      if (found !== undefined) {
        return found;
      }
    }
  }
  
  return undefined;
}

export function normalizeInitialValues<T extends Record<string, any>>(
  initialValues: T | undefined,
  fields: FieldConfig[]
): Record<string, string> {
  const out: Partial<T> = {};

  // If there's no payload, return all configured fields as empty string
  if (!initialValues) {
    fields.forEach((f) => ((out as any)[f.name] = ""));
    return out as Record<string, string>;
  }

  // copy top-level values so unrelated keys remain
  Object.keys(initialValues).forEach((k) => {
    (out as any)[k] = (initialValues as any)[k];
  });

  const formatDateString = (dateStr: string): string => {
    const regex = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/;
    const iso = regex.exec(dateStr);
    return iso ? `${iso[2]}/${iso[3]}/${iso[1]}` : dateStr;
  };

  const formatDate = (date: Date): string => {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = String(date.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  };

  const toDisplay = (v: any): string => {
    if (v === null || v === undefined) {
      return "";
    }
    if (typeof v === "string") {
      const s = v.trim();
      return s === "" ? "" : formatDateString(s);
    }
    if (v instanceof Date && !Number.isNaN(v.getTime())) {
      return formatDate(v);
    }
    return String(v);
  };

  fields.forEach((f) => {
    const name = f.name;
    let val: any;
    
    if (Object.hasOwn(initialValues, name)) {
      val = (initialValues as any)[name];
    } else {
      val = findValueRecursively(initialValues as any, name);
    }
    
    (out as any)[name] = val === undefined ? "" : toDisplay(val);
  });

  return out as Record<string, string>;
}